import { FiAlertCircle } from 'react-icons/fi'

import { Button, Skeleton } from '@vtex/shoreline'

import { formatMoney } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import styles from '../CampaignForm/elements/BudgetPacingNotice/styles.module.css'

type BudgetPacingCyclePreviewProps = {
  remainingCycleAllocation?: number
  newDailyBudget?: number
  remainingDays?: number
  loading?: boolean
  error?: boolean
  onRetry?: () => void
}

const BudgetPacingCyclePreview = ({
  remainingCycleAllocation,
  newDailyBudget,
  remainingDays,
  loading = false,
  error = false,
  onRetry
}: BudgetPacingCyclePreviewProps) => {
  const { t } = useAPPTranslation()

  if (loading) {
    return (
      <div className={styles.box} data-testid="cycle-preview-loading">
        <Skeleton width="220px" height="14px" />
        <Skeleton width="140px" height="28px" />
        <Skeleton width="200px" height="14px" />
      </div>
    )
  }

  if (error) {
    return (
      <div
        className={`${styles.box} d-flex flex-column align-items-center justify-content-center text-center gap-3 py-4`}
        data-testid="cycle-preview-error"
      >
        <FiAlertCircle size={24} />
        <span className={styles.title}>
          {t('rm:budgetPacing.cyclePreview.error')}
        </span>
        {onRetry && (
          <Button onClick={onRetry}>
            {t('rm:budgetPacing.cyclePreview.retry')}
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className={styles.box}>
      <span className={styles.title}>
        {t('rm:budgetPacing.cyclePreview.title')}
      </span>
      <span className={styles.value}>
        {formatMoney(remainingCycleAllocation)}
      </span>
      <div className={styles.formulaRow}>
        <span>=</span>
        <span>{formatMoney(newDailyBudget)}</span>
        <span className={styles.muted}>
          {t('rm:budgetPacing.cyclePreview.formula.allocationLabel')}
        </span>
        <span>
          {t('rm:budgetPacing.cyclePreview.formula.multiplier')} {remainingDays}
        </span>
        <span className={styles.muted}>
          {t('rm:budgetPacing.cyclePreview.formula.daysLabel')}
        </span>
      </div>
    </div>
  )
}

export default BudgetPacingCyclePreview
